import * as _ from 'lodash'
import {
    ISearch,
    ISearchProfile,
    ISearchProvider,
    ISearchRequestSubset,
    ISearchResultsTotalSpecification
} from "./PA.UIM.Core.IF.Search";
import {ISearchRequest} from "../PA.Core/PASearch";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {PAusrContext} from "../PA.Core/PAusrContext";
import {WB_BusinessUnitRow, WB_CustomerRow, WB_SalesOrgRow} from "../PA.Core/PAdbSchemaRows";
import {UserSecurityService} from '../PA.Security.Services/UserSecurityService';

export interface StandardSearchResults {

    wb_id: number | string,
    id_ui: string,
    name: string,

    app_id?: string

}

export interface ISearchUtilsCondition {
    [fieldid: string]: any
}

export interface ISearchUtilsOptions {
    ProfileId?: string,
    Profile?: ISearchProfile,
    OrderByFields?: string[],
    RowLimit?: number,
    app_id?: string,
    bIncludeTotals?: boolean
}

interface TotalValues {
    [id: string]: number,

    rowCount: number
}

interface TotalDict {
    [id: string]: TotalDict | TotalValues
}

export interface IUsrContextScopeField {
    items: any[],
    keyField: string,
    nameField: string,
    defaultKey: number
}

interface IUsrContextScopeFieldSpec {
    listProp: string,
    ctxProp: string,
    keyField: string,
    nameField: string
}

// Search Provider fields representing the Global Scope Settings (MD Set, Company, Sales Org, Sales Area)
// tracked on PAusrContext. Any profile filter using one of these field ids should be rendered as a
// dropdown sourced from the user's scoped lists and defaulted from their Global Scope Settings.
// Both the WB_*_ID convention and the far more common *_NAME convention (which filters by name text,
// not wb id) are covered.
const dctUsrContextScopeFields: { [fieldId: string]: IUsrContextScopeFieldSpec } = {
    WB_MDSET_ID: {listProp: "WB_MasterDataSets", ctxProp: "Context_WB_MasterDataSetRow", keyField: "WB_MDSET_ID", nameField: "MDSET_NAME"},
    MDSET_NAME: {listProp: "WB_MasterDataSets", ctxProp: "Context_WB_MasterDataSetRow", keyField: "MDSET_NAME", nameField: "MDSET_NAME"},

    WB_COMPANY_ID: {listProp: "WB_Companies", ctxProp: "Context_WB_CompanyRow", keyField: "WB_COMPANY_ID", nameField: "COMPANY_NAME"},
    COMPANY_NAME: {listProp: "WB_Companies", ctxProp: "Context_WB_CompanyRow", keyField: "COMPANY_NAME", nameField: "COMPANY_NAME"},

    WB_SALESORG_ID: {listProp: "WB_SalesOrgs", ctxProp: "Context_WB_SalesOrgRow", keyField: "WB_SALESORG_ID", nameField: "SALESORG_NAME"},
    SALESORG_NAME: {listProp: "WB_SalesOrgs", ctxProp: "Context_WB_SalesOrgRow", keyField: "SALESORG_NAME", nameField: "SALESORG_NAME"},

    WB_SALESAREA_ID: {listProp: "WB_SalesAreas", ctxProp: "Context_WB_SalesAreaRow", keyField: "WB_SALESAREA_ID", nameField: "Name"},
    SALESAREA_NAME: {listProp: "WB_SalesAreas", ctxProp: "Context_WB_SalesAreaRow", keyField: "SALESAREA_NAME", nameField: "SALESAREA_NAME"},
}

export class SearchUtils {

    // searchProvider is used to exclude cases where the field id is the search's own entity identity
    // (e.g. MasterDataSetSearch's own "Name" field is MDSET_NAME, but that's the entity being searched
    // for, not a scope reference to it) rather than a cross-reference scope filter.
    static isUsrContextScopeField(fieldId: string, searchProvider?: ISearchProvider): boolean {
        if (!(fieldId in dctUsrContextScopeFields)) {
            return false
        }
        if (_.isEmpty(searchProvider)) {
            return true
        }
        let keyFields = searchProvider.keyFields
        return fieldId != keyFields.id && fieldId != keyFields.id_ui && fieldId != keyFields.name
    }

    static getUsrContextScopeField(fieldId: string): IUsrContextScopeField {
        let spec = dctUsrContextScopeFields[fieldId]
        if (_.isEmpty(spec)) {
            return undefined
        }

        let userContext: PAusrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        let ctxRow: any = (<any>userContext)[spec.ctxProp]

        return {
            items: (<any>userContext)[spec.listProp] || [],
            keyField: spec.keyField,
            nameField: spec.nameField,
            defaultKey: ctxRow ? ctxRow[spec.keyField] : undefined
        }
    }

    static getSearchResults(search: ISearch, condition?: ISearchUtilsCondition, options?: ISearchUtilsOptions): Promise<any[]> {
        return new Promise<any[]>((resolve, error) => {
            let queryCondition = condition || {}
            let opts = options || {}
            let aProfile: ISearchProfile = opts.Profile
            aProfile = aProfile || _.first(_.filter(search.searchProfiles, (iProfile: ISearchProfile) => {
                return iProfile.profileId == opts.ProfileId
            }))
            aProfile = aProfile || _.first(_.filter(search.searchProfiles, (iProfile: ISearchProfile) => {
                return iProfile.profileId == search.searchProfileIdDefault
            }))

            if (_.isEmpty(aProfile)) {
                error("Profile not found")
            }

            let srchRequestSubset: ISearchRequestSubset = {
                where: queryCondition,
                limit: opts.RowLimit || 0
            }
            if ((!_.isEmpty(opts.OrderByFields)) && _.isArray(opts.OrderByFields)) {
                srchRequestSubset.order = opts.OrderByFields
            }


            aProfile.profileSearchProvider.search(srchRequestSubset)
                .then((res: any[]) => {
                    resolve(res)
                })
                .catch((err) => {
                    error(err)
                })

        })
    }

    static getSearchResultCounts(search: ISearch, condition?: ISearchUtilsCondition, options?: ISearchUtilsOptions): Promise<number> {
        return new Promise<number>((resolve, error) => {
            let queryCondition = condition || {}
            let opts = options || {}
            let aProfile: ISearchProfile = opts.Profile
            aProfile = aProfile || _.first(_.filter(search.searchProfiles, (iProfile: ISearchProfile) => {
                return iProfile.profileId == opts.ProfileId
            }))
            aProfile = aProfile || _.first(_.filter(search.searchProfiles, (iProfile: ISearchProfile) => {
                return iProfile.profileId == search.searchProfileIdDefault
            }))

            if (_.isEmpty(aProfile)) {
                error("Profile not found")
            }

            let srchRequestSubset: ISearchRequestSubset = {
                where: queryCondition,
                limit: 0,
                bCountOnly: true
            }

            aProfile.profileSearchProvider.search(srchRequestSubset)
                .then((res: any[]) => {
                    let returnValue = 0
                    if (res.length && 'CNT' in _.first(res))
                        returnValue = Number(_.first(res).CNT)
                    resolve(returnValue)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    static getStandardSearchResults(search: ISearch, condition?: ISearchUtilsCondition, options?: ISearchUtilsOptions): Promise<StandardSearchResults[]> {

        return new Promise<StandardSearchResults[]>((resolve, error) => {

            let opts = options || {}
            let aProfile: ISearchProfile = opts.Profile
            aProfile = aProfile || _.first(_.filter(search.searchProfiles, (iProfile: ISearchProfile) => {
                return iProfile.profileId == opts.ProfileId
            }))
            aProfile = aProfile || _.first(_.filter(search.searchProfiles, (iProfile: ISearchProfile) => {
                return iProfile.profileId == search.searchProfileIdDefault
            }))

            SearchUtils.getSearchResults(search, condition, options)
                .then((res: any[]) => {
                    resolve(SearchUtils.convertSearchResultsToStandardFormat(aProfile.profileSearchProvider, res, opts.app_id))
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    static convertSearchResultsToStandardFormat(searchProvider: ISearchProvider, results: any[], app_id?: string): StandardSearchResults[] {

        if (_.isEmpty(results)) {
            return []
        }

        let returnValue: StandardSearchResults[] = []

        _.forEach(results, (aRow: any) => {
            let newRow = {
                wb_id: aRow[searchProvider.keyFields.id],
                id_ui: aRow[searchProvider.keyFields.id_ui],
                name: aRow[searchProvider.keyFields.name],
                app_id: app_id || ""
            }
            returnValue.push(newRow)
        })

        return returnValue

    }

    static makeSearchRequest(searchRequestSubset: ISearchRequestSubset, sModelName: string, fields: { [id: string]: { type: string } }, distinct?: string[]): ISearchRequest {

        let srs: ISearchRequestSubset = searchRequestSubset || {}
        let returnValue: ISearchRequest = {
            model: sModelName || 'dummy',
            where: srs.where || {},
            limit: srs.limit || 0,
            bCountOnly: srs.bCountOnly || false,
            fields: _.mapValues(fields || {}, (fieldSpec: { type: string }) => {
                return {type: fieldSpec.type}
            })
        }

        if (!_.isEmpty(srs.order) && _.isArray(srs.order)) {
            returnValue.order = srs.order
        }

        if (!_.isEmpty(distinct) && _.isArray(distinct)) {
            returnValue.distinct = distinct
        }
        return returnValue
    }

    static makeSQLSearchRequest(sql: string, where?: string, limit?:number): ISearchRequest {
        let query = sql.replace(/union/gi, 'UNIONALL')
        let returnValue: ISearchRequest = {
            sqlQuery: query,
            whereString: where,
            limit:limit
        }

        return returnValue
    }

    static addUsrContextCustomerFilter(searchRequest: ISearchRequest, sWB_CUST_ID_FieldName: string) {
        let userContext: PAusrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        let arrWBCustID = _.map(userContext.Customers, (aWBCustomer: WB_CustomerRow) => aWBCustomer.cif.WB_CUST_ID)

        SearchUtils.intersectFilter(searchRequest, sWB_CUST_ID_FieldName, arrWBCustID)
    }

    static addUsrContextSalesOrgFilter(searchRequest: ISearchRequest, sWB_SALESORG_ID_FieldName: string) {
        let userContext: PAusrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        let currentWBSalesOrgId = userContext.Context_WB_SalesOrgRow?.cif?.WB_SALESORG_ID
        let arrWBSalesOrgID = _.isNil(currentWBSalesOrgId) ? [-999] : [currentWBSalesOrgId]

        SearchUtils.intersectFilter(searchRequest, sWB_SALESORG_ID_FieldName, arrWBSalesOrgID)
    }

    static addUsrContextBusinessUnitFilter(searchRequest: ISearchRequest, sWB_BusinessUnit_ID_FieldName: string) {
        let userContext: PAusrContext = PAEnvironment.getValue(PAEnvironment.UserContext);
        let arrWBBusinessUnitID = _.map(userContext.BusinessUnits, (aWBBusinessUit: WB_BusinessUnitRow) => aWBBusinessUit.cif.WB_BUSINESSUNIT_ID);

        //SearchUtils.intersectFilter(searchRequest, sWB_BusinessUnit_ID_FieldName, arrWBBusinessUnitID)
    }

    static addUserGroupPlansFilter(searchRequest: ISearchRequest, sWB_Plan_ID_FiledName: string): Promise<void> {
        return new Promise<void>((resolve, error) => {
            UserSecurityService.getUserGroupPlanList()
                .then((arrWBPlanIds: number[]) => {
                    if (_.isEmpty(arrWBPlanIds)) {
                        SearchUtils.intersectFilter(searchRequest, sWB_Plan_ID_FiledName, [-999])
                    } else {
                        SearchUtils.intersectFilter(searchRequest, sWB_Plan_ID_FiledName, arrWBPlanIds)
                    }
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })

        })
    }

    static addUserGroupCustomerFilter(searchRequest: ISearchRequest, sWB_Cust_ID_FiledName: string): Promise<void> {
        return new Promise<void>((resolve, error) => {
            UserSecurityService.getUserGroupCustomerList()
                .then((arrWBCustIds: number[]) => {
                    if (_.isEmpty(arrWBCustIds)) {
                        SearchUtils.intersectFilter(searchRequest, sWB_Cust_ID_FiledName, [-999])
                    } else {
                        SearchUtils.intersectFilter(searchRequest, sWB_Cust_ID_FiledName, arrWBCustIds)
                    }
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })

        })
    }

    static addUserGroupProductGroupFilter(searchRequest: ISearchRequest, sWB_PRDGRP_ID_FiledName: string): Promise<void> {
        return new Promise<void>((resolve, error) => {
            UserSecurityService.getUserGroupProductGroupList()
                .then((arrWBPrdGrpIds: number[]) => {
                    if (_.isEmpty(arrWBPrdGrpIds)) {
                        SearchUtils.intersectFilter(searchRequest, sWB_PRDGRP_ID_FiledName, [-999])
                    } else {
                        SearchUtils.intersectFilter(searchRequest, sWB_PRDGRP_ID_FiledName, arrWBPrdGrpIds)
                    }
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })

        })
    }

    static addUsrContextSalesOrgsFilter(searchRequest: ISearchRequest, sWB_SALESORG_ID_FieldName: string) {
        let userContext: PAusrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        let arrWBSalesOrgID = _.map(userContext.WB_SalesOrgs, (aWBSalesOrg: WB_SalesOrgRow) => aWBSalesOrg.cif.WB_SALESORG_ID)

        if (_.isEmpty(arrWBSalesOrgID)) {
            SearchUtils.intersectFilter(searchRequest, sWB_SALESORG_ID_FieldName, [-999])
        } else {
            SearchUtils.intersectFilter(searchRequest, sWB_SALESORG_ID_FieldName, arrWBSalesOrgID)
        }
    }

    static intersectFilter(searchRequest: ISearchRequest, sFieldName: string, arrIntersecrIDs: number[]) {
        if (_.isEmpty(searchRequest.where)) {
            searchRequest.where = {[sFieldName]: {inq: arrIntersecrIDs}}
        } else if (sFieldName in searchRequest.where) {
            if (_.isNumber(searchRequest.where[sFieldName])) {
                let aSingleId = searchRequest.where[sFieldName]
                // serarch for one ..
                searchRequest.where[sFieldName] = {inq: _.intersection([aSingleId], arrIntersecrIDs)}
            } else if (_.isObject(searchRequest.where[sFieldName])) {
                // Check if in Operator
                let existingCond = searchRequest.where[sFieldName]
                if ('inq' in existingCond) {
                    // rewrite existing condition
                    let cndArr = searchRequest.where[sFieldName].inq
                    searchRequest.where[sFieldName] = {inq: _.intersection(cndArr, arrIntersecrIDs)}
                } else {
                    // fall back
                    searchRequest.where = {and: [{[sFieldName]: {inq: arrIntersecrIDs}}, searchRequest.where]}
                }
            } else {
                // fall back
                searchRequest.where = {and: [{[sFieldName]: {inq: arrIntersecrIDs}}, searchRequest.where]}
            }
        } else {
            // fall back
            searchRequest.where = {and: [{[sFieldName]: {inq: arrIntersecrIDs}}, searchRequest.where]}
        }
    }

    //Q: How do I sort it so that the totals will work out ?  only allow one inline set of totals.
    static applyTotals(input: { [id: string]: any }[], specs: ISearchResultsTotalSpecification[]): { [id: string]: any }[] {

        if (_.isEmpty(specs)) {
            return input
        }

        let inlineSortKeys: string []
        let dctTotalDict: { [id: string]: TotalDict } = {}
        let returnValue: { [id: string]: any }[] = []
        let bInclineSpecExists = false
        _.forEach(specs, (aSpec) => {
            dctTotalDict[aSpec.id] = {}
            if (aSpec.placement == "Inline") {
                bInclineSpecExists = true
                inlineSortKeys = aSpec.orderByFields || aSpec.aggregateByFields
            }
        })


        if (!_.isEmpty(inlineSortKeys)) {
            input = _.sortBy(input, inlineSortKeys)
        }

        _.forEach(input, (aRow, aRowIdx) => {

            _.forEach(specs, (aSpec) => {
                let totalDict: TotalDict | any = dctTotalDict[aSpec.id]
                _.forEach(aSpec.aggregateByFields, (anAggByFieldName) => {
                    let fieldValue = aRow[anAggByFieldName]
                    if (!(fieldValue in totalDict)) {
                        totalDict[fieldValue] = {}
                    }
                    totalDict = <any>totalDict[fieldValue]
                })
                if (_.isEmpty(totalDict)) {
                    // Initialize Total Row
                    totalDict.rowCount = 0
                    _.forEach(aSpec.aggregateByFields || [], (anAggField) => {
                        totalDict[anAggField] = aRow[anAggField]
                    })
                    _.forEach(aSpec.sumFields || [], (aSumFieldName) => {
                        totalDict[aSumFieldName] = 0
                    })
                    _.forEach(aSpec.minFields || [], (aMinFieldName) => {
                        totalDict[aMinFieldName] = _.toNumber(aRow[aMinFieldName])
                    })
                    _.forEach(aSpec.maxFields || [], (aMaxFieldName) => {
                        totalDict[aMaxFieldName] = _.toNumber(aRow[aMaxFieldName])
                    })
                    _.forEach(aSpec.countFields || [], (aCountFieldName) => {
                        totalDict[aCountFieldName] = {}
                    })
                    _.forEach(aSpec.firstValueFields || [], (aFirstFieldName) => {
                        totalDict[aFirstFieldName] = aRow[aFirstFieldName]
                    })
                    _.forEach(aSpec.constantFieldValues || [], (value, key) => {
                        totalDict[key] = value
                    })
                }
                //

                totalDict.rowCount += 1
                _.forEach(aSpec.sumFields || [], (aSumFieldName) => {
                    totalDict[aSumFieldName] += _.toNumber(aRow[aSumFieldName])
                })
                _.forEach(aSpec.countFields || [], (aCountFieldName) => {
                    totalDict[aCountFieldName][aRow[aCountFieldName]] = true
                })
                _.forEach(aSpec.minFields || [], (aMinFieldName) => {
                    totalDict[aMinFieldName] = Math.min(_.toNumber(aRow[aMinFieldName]), totalDict[aMinFieldName])
                })
                _.forEach(aSpec.maxFields || [], (aMaxFieldName) => {
                    totalDict[aMaxFieldName] = Math.max(_.toNumber(aRow[aMaxFieldName]), totalDict[aMaxFieldName])
                })


            })  // Next Specification


            returnValue.push(aRow)

            if (bInclineSpecExists) {
                let aSpec = _.first(_.filter(specs, (s) => {
                    return s.placement == "Inline"
                }))
                if (aSpec) {
                    // Check if this is the last record or the next record is different
                    let bInsertInlineRecord = (aRowIdx == (input.length - 1)) // Normally false, true for last record
                    let nextRecord = input[aRowIdx + 1] || {}
                    _.forEach(aSpec.aggregateByFields, (anAggFieldName) => {
                        if (aRow[anAggFieldName] != nextRecord[anAggFieldName]) {
                            bInsertInlineRecord = true
                            return false
                        }
                    })

                    if (bInsertInlineRecord) {
                        let totalDict: TotalDict | any = dctTotalDict[aSpec.id]
                        _.forEach(aSpec.aggregateByFields, (anAggByFieldName) => {
                            let fieldValue = aRow[anAggByFieldName]
                            if (!(fieldValue in totalDict)) {
                                totalDict[fieldValue] = {}
                            }
                            totalDict = <any>totalDict[fieldValue]
                        })

                        // convert count fields
                        _.forEach(aSpec.countFields || [], (aCountField) => {
                            totalDict[aCountField] = _.keys(totalDict[aCountField] || {}).length
                        })

                        totalDict.AggregationId = aSpec.id
                        totalDict.AggregationRow = true
                        returnValue.push(totalDict)
                    }

                }
            }


        }) // Next Row

        // Add Non-Inline Aggregation Rows..
        _.forEach(specs, (aSpec) => {
            if (aSpec.placement == "Inline") {
                return
            }

            let summaryRows = SearchUtils.getTotalRows(aSpec, dctTotalDict)
            switch (aSpec.placement) {
                case "Begin":
                    returnValue.unshift.apply(returnValue, summaryRows)
                    break;
                case "End":
                    returnValue.push.apply(returnValue, summaryRows)
                    break;
            }

        })


        return returnValue
    }

    //
    private static getTotalRows(spec: ISearchResultsTotalSpecification, dctTotalDict: { [id: string]: TotalDict }): { [id: string]: any }[] {
        //
        let outputRows = [dctTotalDict[spec.id]]
        _.forEach(spec.aggregateByFields, (anAggField) => {
            let newOutputRows: any[] = []
            _.forEach(outputRows, (aDct) => {
                newOutputRows.push.apply(newOutputRows, _.values(aDct))
            })
            outputRows = newOutputRows
        })

        _.forEach(outputRows, (aRow: any) => {

            // Convert count values
            _.forEach(spec.countFields || [], (aCountField) => {
                aRow[aCountField] = _.keys(aRow[aCountField] || {}).length
            })

            aRow.AggregationId = spec.id
            aRow.AggregationRow = true
        })

        // Sort
        let arrSortColumns = spec.orderByFields || spec.aggregateByFields
        if (!_.isEmpty(arrSortColumns)) {
            outputRows = _.sortBy(outputRows, arrSortColumns)
        }

        return outputRows
    }


}
